/**
 * 日付操作ユーティリティ関数
 * (既存のSpaceDayクラス相当の機能を提供)
 */

import type { AvailabilitySymbol } from './types';

// 曜日の表示用文字列（日本語）
export const WEEK_DAYS = ['日', '月', '火', '水', '木', '金', '土'];

/**
 * 現在の日付を取得（日本時間の0時0分0秒）
 * @returns {Date} 日本時間の現在日
 */
export const today = (): Date => {
  // 現在の日本時間を取得
  const now = new Date();
  // 日本時間の0時0分0秒に設定（時分秒ミリ秒をリセット）
  now.setHours(0, 0, 0, 0);
  return now;
};

/**
 * 指定された日数分後の日付を取得
 * @param date 基準日
 * @param days 加算する日数
 * @returns 計算後の日付
 */
export const addDays = (date: Date, days: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + days);
  return result;
};

/**
 * 指定した日付に週数を加算
 * @param {Date} date 基準日
 * @param {number} weeks 加算する週数（負の値で減算）
 * @returns {Date} 計算後の日付
 */
export const addWeeks = (date: Date, weeks: number): Date => {
  const result = new Date(date);
  result.setDate(result.getDate() + (weeks * 7));
  return result;
};

/**
 * 日付を YYYY/MM/DD 形式でフォーマット
 * @param {Date} date 日付
 * @returns {string} フォーマットされた日付文字列
 */
export const formatDate = (date: Date): string => {
  const year = date.getFullYear();
  // 月と日は0埋めして2桁に
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}/${month}/${day}`;
};

/**
 * 日付を YYYY-MM-DD 形式でフォーマット（API用）
 * @param {Date} date 日付
 * @returns {string} フォーマットされた日付文字列
 */
export const formatAPIDate = (date: Date): string => {
  const year = date.getFullYear();
  // 月と日は0埋めして2桁に
  const month = String(date.getMonth() + 1).padStart(2, '0');
  const day = String(date.getDate()).padStart(2, '0');
  return `${year}-${month}-${day}`;
};

/**
 * 日付から表示用のオブジェクトを作成
 * @param date 日付
 * @returns 表示用オブジェクト（年、月、日、曜日）
 */
export const dateToObject = (date: Date) => {
  return {
    year: date.getFullYear(),
    month: date.getMonth() + 1,
    date: date.getDate(),
    day: WEEK_DAYS[date.getDay()]
  };
};

/**
 * 2つの日付の日数差を計算
 * @param {Date} startDate 開始日
 * @param {Date} endDate 終了日
 * @returns {number} 日数差
 */
export const daysDifference = (startDate: Date, endDate: Date): number => {
  // 時分秒ミリ秒を無視して日数の差だけを計算
  const start = new Date(startDate);
  start.setHours(0, 0, 0, 0);

  const end = new Date(endDate);
  end.setHours(0, 0, 0, 0);

  // ミリ秒単位での差を日数に変換
  const diffTime = end.getTime() - start.getTime();
  return Math.floor(diffTime / (1000 * 60 * 60 * 24));
};

/**
 * 時刻文字列から分数に変換（"HH:MM" -> minutes）
 * @param timeStr 時刻文字列 (例: "10:30")
 * @returns 0時からの経過分数
 */
export const timeToMinutes = (timeStr: string): number => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  return hours * 60 + minutes;
};

/**
 * ある日付の特定の時刻に相当するDateオブジェクトを作成
 * @param date 日付
 * @param timeStr 時刻文字列 (例: "10:30")
 * @returns 指定された日時のDateオブジェクト
 */
export const dateWithTime = (date: Date, timeStr: string): Date => {
  const [hours, minutes] = timeStr.split(':').map(Number);
  const result = new Date(date);
  result.setHours(hours, minutes, 0, 0);
  return result;
};

/**
 * 曜日名を取得（日本語）
 * @param {Date} date 日付
 * @returns {string} 曜日名
 */
export const getWeekdayName = (date: Date): string => {
  const weekdays = ['日', '月', '火', '水', '木', '金', '土'];
  return weekdays[date.getDay()];
};

/**
 * 今日から指定日までの日数を計算（reserve_days_ahead制限チェック用）
 * @param {Date} targetDate 対象日
 * @returns {number} 今日から対象日までの日数（今日=0, 明日=1）
 */
export const daysDifferenceFromToday = (targetDate: Date): number => {
  return daysDifference(today(), targetDate);
};

/**
 * 日付が含まれている文字列から日付部分のみを抽出
 * @param {string} dateStr 日付を含む文字列
 * @returns {string} 抽出された日付文字列
 */
export const extractDatePart = (dateStr: string): string => {
  const match = dateStr.match(/\d{4}[/-]\d{2}[/-]\d{2}/);
  return match ? match[0] : '';
};

/**
 * 日付文字列をDateオブジェクトに変換
 * @param {string} dateStr 日付文字列（YYYY-MM-DD or YYYY/MM/DD）
 * @returns {Date} 変換されたDateオブジェクト
 */
export const parseDate = (dateStr: string): Date => {
  // ハイフンをスラッシュに置換して処理を統一（どちらの形式でも対応）
  const normalizedDateStr = dateStr.replace(/-/g, '/');
  return new Date(normalizedDateStr);
};

/**
 * 2つの日付が同じ日かどうかを判定
 * @param {Date} date1 日付1
 * @param {Date} date2 日付2
 * @returns {boolean} 同じ日ならtrue
 */
export const isSameDay = (date1: Date, date2: Date): boolean => {
  return (
    date1.getFullYear() === date2.getFullYear() &&
    date1.getMonth() === date2.getMonth() &&
    date1.getDate() === date2.getDate()
  );
};

/**
 * 指定した日付が今日かどうかを判定
 * @param {Date} date 判定する日付
 * @returns {boolean} 今日ならtrue
 */
export const isToday = (date: Date): boolean => {
  return isSameDay(date, today());
};

/**
 * 指定した日付が週末（土日）かどうかを判定
 * @param {Date} date 判定する日付
 * @returns {boolean} 週末ならtrue
 */
export const isWeekend = (date: Date): boolean => {
  const day = date.getDay();
  return day === 0 || day === 6; // 0:日曜日, 6:土曜日
};

/**
 * 空き状況のシンボルを計算
 * @param {number} availableHours 利用可能時間
 * @param {boolean} isOverLimit 予約期間制限を超えているか
 * @returns {AvailabilitySymbol} 空き状況シンボル
 */
export const calculateAvailabilityStatus = (
  availableHours: number,
  isOverLimit: boolean
): AvailabilitySymbol => {
  // 予約受付期間（reserve_days_ahead）を超えている場合は利用不可
  if (isOverLimit) {
    return 'not-open';
  }

  // 利用可能時間に応じてシンボルを決定
  if (availableHours >= 7) {
    return 'open';    // 〇 - 7時間以上の空き
  }

  if (availableHours >= 3) {
    return 'average'; // △ - 3〜7時間未満の空き
  }

  if (availableHours > 0) {
    return 'few';     // × - 3時間未満の空き
  }

  return 'not-open';  // × - 利用不可（空きなし）
};